import { value } from '@takeshape/util';
import { CURRENT_OPEN_API_TRANSFORM_VERSION } from "../constants.js";
import { updateAuthentication } from "../util/authentication.js";
import { hasEveryKey } from "../util/has.js";
import { endpointWithPath, getHealthCheck } from "../util/health.js";
const title = 'Anthropic';
const specUrl = 'https://raw.githubusercontent.com/takeshape/openapi-specs/d1219888103fdd900138589e4e03c0b216c0a5d1/specs/anthropic/2023-06-01.yml';
const serviceProvider = {
    name: 'anthropic',
    title,
    description: "Anthropic is an AI safety and research company that's working to build reliable, interpretable, and steerable AI systems.",
    categories: ['ai'],
    links: [
        { title: 'Homepage', url: 'https://www.anthropic.com/' },
        { title: 'Docs', url: '/docs/services/providers/anthropic' }
    ],
    serviceType: 'openapi',
    introspectionType: 'openapi',
    createId: value('anthropic'),
    getFormSchema: value({
        type: 'object',
        required: ['title', 'id', 'namespace', 'authentication'],
        properties: {
            title: {
                title: 'Name',
                type: 'string',
                default: 'Anthropic'
            },
            id: {
                title: 'Slug',
                type: 'string',
                pattern: '[0-9a-z_-]+',
                default: 'anthropic'
            },
            namespace: {
                title: 'Namespace',
                type: 'string',
                pattern: '^[A-Z][A-Za-z]+$',
                default: 'Anthropic'
            },
            authentication: {
                title: 'Authentication',
                description: 'The following properties are encrypted and are obscured on this form to ensure the security of your data.',
                type: 'object',
                properties: {
                    token: {
                        title: 'API Key',
                        type: 'string',
                        '@sensitive': true
                    }
                },
                required: ['token']
            }
        }
    }),
    getFormConfig: value({
        order: ['title', 'id', 'namespace', 'authentication'],
        properties: {
            title: {
                widget: 'singleLineText',
                instructions: 'A human-readable title for this service.'
            },
            id: {
                widget: 'singleLineText',
                instructions: 'The human-readable unique identifier for this service. Only lowercase letters, numbers, hyphen, and underscore are allowed.'
            },
            namespace: {
                widget: 'singleLineText',
                instructions: 'A namespace to use for any generated types or queries. For example, if you enter `Foo` your types will be named `Foo_Bar`.'
            },
            authentication: {
                properties: {
                    token: {
                        widget: 'sensitiveField',
                        instructions: 'You can manage API keys for Anthropic at https://console.anthropic.com/settings/keys'
                    }
                },
                order: ['token']
            }
        }
    }),
    getConfig(formData) {
        const config = {
            title: formData.title ?? title,
            namespace: formData.namespace ?? title,
            provider: 'anthropic',
            serviceType: 'openapi',
            authenticationType: 'bearer',
            options: {
                endpoint: 'https://api.anthropic.com/v1',
                spec: specUrl,
                headers: {
                    'anthropic-version': '2023-06-01'
                },
                transformVersion: CURRENT_OPEN_API_TRANSFORM_VERSION
            }
        };
        return updateAuthentication(config, formData, {
            type: 'bearer',
            header: 'x-api-key'
        });
    },
    isCompleteConfig: hasEveryKey([
        'authentication',
        'options.endpoint',
        'options.spec',
        'options.headers.anthropic-version'
    ]),
    getHealthCheck: getHealthCheck({
        checkName: 'ping',
        endpoint: endpointWithPath('/messages'),
        requestMethod: 'POST',
        healthyResponseStatusCode: 400
    }),
    getAiModels() {
        return {
            generate: [
                // Claude 4.6
                'claude-sonnet-4-6',
                'claude-opus-4-6',
                // Claude 4.5
                'claude-haiku-4-5-20251001',
                'claude-sonnet-4-5-20250929',
                'claude-opus-4-5-20251101',
                // Claude 4.1
                'claude-opus-4-1-20250805',
                // Claude 4.0
                'claude-sonnet-4-20250514',
                'claude-opus-4-20250514'
            ],
            embedding: [],
            image: []
        };
    },
    getAiCacheControlOptions(options) {
        const { ttl } = options ?? {};
        return {
            providerOptions: {
                anthropic: {
                    cacheControl: {
                        type: 'ephemeral',
                        ...(ttl && { ttl })
                    }
                }
            }
        };
    }
};
export default serviceProvider;
